import { ReactNode, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import { PageLayout } from './PageLayout';
import { HubShellContext } from './HubShellContext';
import { cn } from '@/utils';

export interface HubTab {
  /** Stable id used for the `?section=` param and the React key. */
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Render the tab body. */
  render: () => ReactNode;
}

interface HubShellProps {
  title: string;
  description: string;
  icon?: LucideIcon;
  /** List of tabs shown as a horizontal sub-nav. Order = display order. */
  tabs: HubTab[];
  /**
   * Section id that should be considered the "default" when no `?section=`
   * is present in the URL. Defaults to the first tab.
   */
  defaultSection?: string;
  /**
   * Optional className for the tab strip wrapper.
   */
  tabsClassName?: string;
  /**
   * Optional content rendered below the tab strip but above the active tab
   * body. Useful for hub-wide summaries, skill rails, or quick actions.
   */
  banner?: ReactNode;
}

/**
 * HubShell — the layout primitive used by every top-level hub page.
 */
export function HubShell({
  title,
  description,
  icon,
  tabs,
  defaultSection,
  banner,
  tabsClassName,
}: HubShellProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const fallback = defaultSection ?? tabs[0]?.id ?? '';
  const requested = searchParams.get('section');
  const activeId = useMemo(() => {
    if (requested && tabs.some((t) => t.id === requested)) return requested;
    return fallback;
  }, [requested, tabs, fallback]);

  useEffect(() => {
    if (requested === activeId) return;
    const next = new URLSearchParams(searchParams);
    next.set('section', activeId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const activeTab = tabs.find((t) => t.id === activeId) ?? tabs[0];

  const handleSelect = (id: string) => {
    if (id === activeId) return;
    const next = new URLSearchParams(searchParams);
    next.set('section', id);
    // Memory owns `q` (search text) and `view` (sub-filter). Drop them when leaving
    // so other hub tabs are not trapped behind a stale memory deep link.
    if (activeId === 'memory' && id !== 'memory') {
      next.delete('q');
      next.delete('view');
    }
    if (activeId === 'search' && id !== 'search') {
      next.delete('q');
      next.delete('date');
      next.delete('cat');
      next.delete('tags');
      next.delete('starred');
      next.delete('sort');
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <PageLayout title={title} description={description} icon={icon}>
      <div className={cn('flex flex-col gap-6', tabsClassName)}>
        {tabs.length > 1 && (
          <div className="sticky top-0 z-30 -mx-6 bg-khiimori-bg-primary px-6 py-2 lg:-mx-8 lg:px-8">
            <div
              role="tablist"
              aria-label={`${title} sections`}
              className="no-scrollbar flex items-center gap-1 overflow-x-auto rounded-xl border border-khiimori-border bg-khiimori-bg-elevated p-1"
            >
              {tabs.map((tab) => {
                const isActive = tab.id === activeId;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleSelect(tab.id)}
                    className={cn(
                      'flex shrink-0 items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary-500/15 text-primary-300 shadow-sm'
                        : 'text-khiimori-text-secondary/60 hover:bg-khiimori-bg-tertiary/50 hover:text-khiimori-text-secondary/80',
                    )}
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                    <span className="whitespace-nowrap">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {banner}

        <HubShellContext.Provider value={true}>
          <div key={activeTab?.id ?? 'empty'} className="hub-embedded-content">
            {activeTab?.render()}
          </div>
        </HubShellContext.Provider>
      </div>
    </PageLayout>
  );
}

/**
 * Helper hook for navigating to a hub section. Useful for "View all" links
 * inside a hub sub-page.
 */
export function useHubNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  return (path: string, section?: string) => {
    const target = section ? `${path}?section=${encodeURIComponent(section)}` : path;
    if (target === location.pathname + location.search) return;
    navigate(target);
  };
}
